import Breadcrumbs from "../components/Breadcrumbs";
import Eyebrow from "../components/Eyebrow";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../lib/seo";
import { SITE } from "../lib/site";

export default function Privacy() {
  usePageMeta({
    title: "Privacy Policy",
    description:
      "How CareCraft collects, uses, and protects the personal information families share when reaching out about senior care.",
    path: "/privacy",
  });

  return (
    <section className="bg-warmwhite pt-[72px]">
      <div className="mx-auto max-w-site px-4 py-14 sm:px-6 md:py-20 lg:px-12">
        <Breadcrumbs trail={[{ label: "Privacy Policy" }]} />
        <div className="mt-10 max-w-3xl">
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl font-medium tracking-tight text-forest sm:text-5xl">
            Privacy Policy
          </h1>
          <p className="mt-6 font-serif text-2xl italic text-forest">
            Your privacy matters to us.
          </p>
          <p className="mt-4 text-lg leading-[1.7] text-charcoal/80">
            When you contact CareCraft, you may be sharing personal information about yourself or
            someone you care for. We understand that some of these conversations can be personal, and
            we want you to know how the information you provide is handled.
          </p>

          <div className="mt-12 space-y-12">
            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">
                Information you may share with us
              </h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                When you call, email, or submit a form through our website, we may receive information
                such as your:
              </p>
              <ul className="mt-4 space-y-2.5 leading-[1.65] text-charcoal/80">
                {[
                  "Name",
                  "Phone number",
                  "Email address",
                  "General information about the person who needs care",
                  "Care preferences or circumstances you choose to share",
                  "Preferred location or type of care",
                  "Timing or urgency of the care need",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="mt-[0.6em] h-1.5 w-1.5 shrink-0 rounded-full bg-clay" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-4 leading-[1.7] text-charcoal/80">
                We encourage families to share only the information that is reasonably needed for us
                to understand the situation and help identify appropriate care options.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">How we use your information</h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                We use the information you provide to understand what kind of support you are looking
                for, communicate with you, and help identify care options that may be appropriate for
                your family.
              </p>
              <p className="mt-4 leading-[1.7] text-charcoal/80">
                When needed to assist with a placement or referral, relevant information may be shared
                with care providers or communities being considered. We aim to share only information
                that is reasonably necessary to help with that process.
              </p>
              <p className="mt-4 font-medium text-forest">We do not sell your personal information.</p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">Website information</h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                Like many websites, the CareCraft website may collect basic technical information
                about how the site is used, such as browser type, device information, pages visited,
                and general website activity. If CareCraft uses website analytics, cookies, or similar
                tools, this Privacy Policy will identify them as appropriate and explain their
                purpose.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">Keeping information secure</h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                We take reasonable steps to protect the personal information shared with CareCraft. At
                the same time, no website, email system, or electronic method of communication can be
                guaranteed to be completely secure.
              </p>
              <p className="mt-4 leading-[1.7] text-charcoal/80">
                Please avoid sending highly sensitive medical records, Social Security numbers,
                financial account information, or other information that is not necessary through the
                website's general contact form.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">
                Information about someone you care for
              </h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                Sometimes a family member, caregiver, or professional contacts CareCraft on behalf of
                another person. If you provide information about someone else, please share only what
                is reasonably necessary for CareCraft to understand the care situation and assist you.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">
                Other websites and care providers
              </h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                Our website or communications may sometimes connect you with independent care
                communities, service providers, or other websites. Those organizations have their own
                privacy practices. Once you provide information directly to another organization, its
                privacy policy and practices will apply to the information you provide to them.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">Your privacy choices</h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                You are welcome to contact CareCraft if you would like to ask what personal
                information you have provided, request a correction, or ask us to delete information
                we maintain about you, where applicable. You may also contact us if you have questions
                about how your information has been used or shared.
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">
                Changes to this Privacy Policy
              </h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                We may update this Privacy Policy from time to time as our website or services change.
                When we do, we will update the effective date shown on this page.
              </p>
              <p className="mt-4 rounded-xl border border-inkline bg-ivory p-5 font-medium text-forest">
                Effective Date: [Website Launch Date]
              </p>
            </Reveal>

            <Reveal>
              <h2 className="font-serif text-2xl font-medium text-forest">
                Questions about your privacy?
              </h2>
              <p className="mt-3 leading-[1.7] text-charcoal/80">
                If you have a question about this Privacy Policy or about information you have shared
                with CareCraft, please contact us.
              </p>
              <div className="mt-5 rounded-2xl border border-inkline bg-ivory p-6">
                <p className="font-serif text-xl font-medium text-forest">{SITE.name}</p>
                <ul className="mt-4 space-y-2.5 text-[0.95rem]">
                  <li>
                    <a
                      href={`mailto:${SITE.email}`}
                      data-testid="privacy-email"
                      className="font-medium text-forest transition-colors hover:text-clay"
                    >
                      {SITE.email}
                    </a>
                  </li>
                  <li>
                    <a
                      href={SITE.phoneHref}
                      data-testid="privacy-phone"
                      className="font-medium text-forest transition-colors hover:text-clay"
                    >
                      {SITE.phoneDisplay}
                    </a>
                  </li>
                  <li className="text-charcoal/75">{SITE.area}</li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
